/**
 * FiberSignalIndicator - Shows signal strength from OLT for a selected fiber
 * Displays real-time optical path trace and power calculation
 */

import React, { useMemo } from 'react';
import { traceOpticalPath, OpticalPathResult } from '../../utils/opticalPathTracer';
import { usePoleStore, useCableStore } from '../../stores';
import './FiberSignalIndicator.css';

interface FiberSignalIndicatorProps {
  cableId: number | undefined;
  fiberNumber: number | undefined;
  compact?: boolean; // If true, show minimal info
}

export const FiberSignalIndicator: React.FC<FiberSignalIndicatorProps> = ({
  cableId,
  fiberNumber,
  compact = false,
}) => {
  const { poles } = usePoleStore();
  const { cables } = useCableStore();

  const pathResult: OpticalPathResult | null = useMemo(() => {
    if (!cableId || !fiberNumber) {
      return null;
    }

    return traceOpticalPath(cableId, fiberNumber, poles, cables);
  }, [cableId, fiberNumber, poles, cables]);

  if (!cableId || !fiberNumber) {
    return null;
  }

  if (!pathResult) {
    return null;
  }

  if (!pathResult.found || !pathResult.isValid) {
    // No signal - cable is disconnected
    if (compact) {
      return (
        <div className="fiber-signal-indicator no-signal compact">
          <span className="signal-icon">⚫</span>
          <span className="signal-text">No OLT signal</span>
        </div>
      );
    }

    return (
      <div className="fiber-signal-indicator no-signal">
        <div className="signal-header">
          <span className="signal-icon">⚫</span>
          <span className="signal-title">No OLT Connection</span>
        </div>
        <div className="signal-message">
          {pathResult.errorMessage || 'Toto vlákno nie je pripojené k OLT'}
        </div>
      </div>
    );
  }

  // Has signal - show power and path
  const signalStrength = getSignalStrength(pathResult.finalPower);
  const signalClass = signalStrength.class;

  if (compact) {
    return (
      <div className={`fiber-signal-indicator ${signalClass} compact`}>
        <span className="signal-icon">{signalStrength.icon}</span>
        <span className="signal-text">
          {pathResult.finalPower.toFixed(1)} dBm
        </span>
        <span className="signal-loss">
          (-{pathResult.totalLoss.toFixed(1)} dB)
        </span>
      </div>
    );
  }

  return (
    <div className={`fiber-signal-indicator ${signalClass}`}>
      <div className="signal-header">
        <span className="signal-icon">{signalStrength.icon}</span>
        <span className="signal-title">{signalStrength.label}</span>
      </div>

      <div className="signal-details">
        <div className="signal-row">
          <span className="signal-label">OLT Power:</span>
          <span className="signal-value">{pathResult.oltPower.toFixed(1)} dBm</span>
        </div>
        <div className="signal-row">
          <span className="signal-label">Celkový útlm:</span>
          <span className="signal-value loss">-{pathResult.totalLoss.toFixed(2)} dB</span>
        </div>
        <div className="signal-row">
          <span className="signal-label">Dostupný výkon:</span>
          <span className="signal-value power">{pathResult.finalPower.toFixed(1)} dBm</span>
        </div>
      </div>

      <div className="signal-path">
        <div className="path-title">Optical Path:</div>
        <div className="path-segments">
          {pathResult.segments.map((segment, index) => (
            <div key={index} className="path-segment">
              <span className="segment-icon">
                {getSegmentIcon(segment.type)}
              </span>
              <span className="segment-description">{segment.description}</span>
              <span className="segment-loss">-{segment.loss.toFixed(2)} dB</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function getSignalStrength(power: number): {
  class: string;
  icon: string;
  label: string;
} {
  if (power >= -8) {
    return { class: 'excellent', icon: '🟢', label: 'Excellent Signal' };
  } else if (power >= -15) {
    return { class: 'good', icon: '🟡', label: 'Good Signal' };
  } else if (power >= -25) {
    return { class: 'fair', icon: '🟠', label: 'Fair Signal' };
  } else if (power >= -28) {
    return { class: 'weak', icon: '🔴', label: 'Weak Signal' };
  } else {
    return { class: 'critical', icon: '⛔', label: 'Critical - Too Weak' };
  }
}

function getSegmentIcon(type: string): string {
  switch (type) {
    case 'olt_port':
      return '🔌';
    case 'olt_splitter':
      return '🔀';
    case 'cable':
      return '📡';
    case 'splitter':
      return '🔀';
    case 'splice':
      return '🔗';
    case 'passthrough':
      return '➡️';
    default:
      return '•';
  }
}

